import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";

export const SiEcriture = ({ module, children }) => {
  const { peutEcrire } = useAuth();
  if (!peutEcrire(module)) return null;
  return children;
};

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
      <div>
        <h1 className="text-2xl font-semibold text-[--idc-text]">{title}</h1>
        {subtitle && <p className="text-sm text-[--idc-muted] mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

const TAG_FORFAIT_COULEURS = {
  "Barémé constructeur": { bg: "#EAF1FE", fg: "#2F6FED" },
  "Offre promotionnelle": { bg: "#FEE2E2", fg: "#B91C1C" },
  "Entretien courant": { bg: "#ECFDF5", fg: "#047857" },
  "Saisonnier": { bg: "#FEF3C7", fg: "#92400E" },
};
export const TAGS_FORFAIT_SUGGERES = Object.keys(TAG_FORFAIT_COULEURS);

export function TagForfait({ tag, onRemove }) {
  const c = TAG_FORFAIT_COULEURS[tag] || { bg: "#EEF1F6", fg: "#5B6B84" };
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap" style={{ background: c.bg, color: c.fg }} data-testid={`tag-forfait-${tag}`}>
      {tag}
      {onRemove && <button onClick={onRemove} className="hover:opacity-60 font-bold" data-testid={`tag-forfait-suppr-${tag}`}>×</button>}
    </span>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="idc-card p-4 space-y-3" data-testid="skeleton-loader">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="skl h-5" style={{ width: `${100 / cols}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardsSkeleton({ n = 4 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="skeleton-loader">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="idc-card p-4 space-y-3">
          <div className="skl h-32 w-full" />
          <div className="skl h-5 w-2/3" />
          <div className="skl h-4 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, titre, message, action }) {
  return (
    <div className="idc-card flex flex-col items-center justify-center py-14 px-6 text-center" data-testid="empty-state">
      {Icon && <Icon className="w-10 h-10 text-[--idc-muted] opacity-40 mb-3" />}
      <p className="font-semibold text-[15px]">{titre}</p>
      {message && <p className="text-sm text-[--idc-muted] mt-1 max-w-md">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="idc-card border-red-200 bg-red-50/50 flex flex-col items-center py-10 px-6 text-center" data-testid="error-state">
      <p className="font-semibold text-[--idc-danger]">Une erreur est survenue</p>
      <p className="text-sm text-[--idc-muted] mt-1">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-3 text-sm font-medium text-[--idc-accent] hover:underline" data-testid="retry-btn">
          Réessayer
        </button>
      )}
    </div>
  );
}

export function Stepper({ etapes, courante }) {
  const idx = etapes.findIndex((e) => e.value === courante);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="stepper">
      {etapes.map((e, i) => (
        <div key={e.value} className="flex items-center gap-1">
          <div className="flex items-center gap-1.5">
            <div
              className="stepper-dot"
              style={{
                background: i < idx ? "#DCFCE7" : i === idx ? "var(--idc-primary)" : "#EEF0F4",
                color: i < idx ? "#16A34A" : i === idx ? "#fff" : "#9AA4B2",
              }}
            >
              {i < idx ? "✓" : i + 1}
            </div>
            <span className={`text-xs font-medium ${i === idx ? "text-[--idc-primary]" : "text-[--idc-muted]"}`}>
              {e.label}
            </span>
          </div>
          {i < etapes.length - 1 && <div className="w-6 h-px bg-[--idc-border] mx-1" />}
        </div>
      ))}
    </div>
  );
}
